/**
 * Keeps the connection-state EVENT from reaching subscribers in a different order from the writes
 * to the connection state itself (deferred finding df-c281f1fe39ea).
 *
 * OnlineStateService writes its connection state at once, but on a flip from Offline to Online it
 * refreshes the CouchDB login before it tells anybody — and that login can take seconds. If a newer
 * check finds the network gone in the meantime, it writes Offline and announces Offline straight
 * away. Then the old login finishes and announces Online. Every subscriber is left believing the
 * app is online while the service itself says offline, and nothing corrects them until the state
 * changes again.
 *
 * The rule is: an announcement goes out only if nothing has written the state since the write it
 * announces. Whoever wrote later has announced, or will announce, its own value, so the last thing
 * subscribers hear always matches the state that holds.
 *
 * This is about WRITE order, not about the order the connectivity checks started in
 * (ProbeResultGate handles that, and decides who may write at all). Every write counts here,
 * including the ones that are deliberately not gated on probe order, such as the demotion to
 * Offline after a failed login.
 *
 * This holds no connection state of its own. It only numbers the writes.
 */
export class ConnectionStatePublishGate {
  /** The number of the most recent write. 0 before anything has written. */
  private latestWriteNumber = 0;

  /** Record a write to the connection state. Call it at the moment of the write. */
  recordWrite(): number {
    return ++this.latestWriteNumber;
  }

  /** Whether the write numbered `writeNumber` is still the most recent one, so announcing its value
   *  cannot overwrite newer news. */
  isLatestWrite(writeNumber: number): boolean {
    return writeNumber === this.latestWriteNumber;
  }
}
